import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { authClient } from '../auth-client.ts';

/** Staff must turn on two-step sign-in before they can use anything else. */
export function TwoFactorSetupPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [setup, setSetup] = useState<{
    uri: string;
    qr: string;
    secret: string;
    backupCodes: string[];
  } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const r = await authClient.twoFactor.enable({ password });
    if (r.error || !r.data || r.data.method !== 'totp') return setError(t('login.failed'));
    const uri = r.data.totpURI;
    setSetup({
      uri,
      qr: await QRCode.toDataURL(uri, { margin: 1, width: 220 }),
      secret: new URL(uri).searchParams.get('secret') ?? '',
      backupCodes: r.data.backupCodes,
    });
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    const r = await authClient.twoFactor.verifyTotp({ code: code.trim() });
    if (r.error) return setError(t('login.badCode'));
    setVerified(true);
  };

  return (
    <main className="narrow" id="main">
      <div className="card">
        <h2>{t('twofa.title')}</h2>
        <p>{t('twofa.intro')}</p>
        {!setup && (
          <form onSubmit={start}>
            <p className="k">{t('twofa.step1')}</p>
            <label>
              {t('login.password')}
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && (
              <p className="err" role="alert">
                {error}
              </p>
            )}
            <button className="btn">{t('twofa.continue')}</button>
          </form>
        )}
        {setup && !verified && (
          <form onSubmit={verify}>
            <p className="k">{t('twofa.step2')}</p>
            <img src={setup.qr} alt="QR" width={220} height={220} />
            <p className="k">
              {t('twofa.key')}: <code>{setup.secret}</code>
            </p>
            <label>
              {t('twofa.step3')}
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            {error && (
              <p className="err" role="alert">
                {error}
              </p>
            )}
            <button className="btn">{t('login.verify')}</button>
          </form>
        )}
        {verified && setup && (
          <div>
            <p className="ok" role="status">
              {t('twofa.done')}
            </p>
            <h3>{t('twofa.backup')}</h3>
            <p className="k">{t('twofa.backupIntro')}</p>
            <div className="codes">
              {setup.backupCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <p>
              <button
                className="btn"
                onClick={() => void qc.invalidateQueries({ queryKey: ['me'] })}
              >
                {t('twofa.continue')}
              </button>
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
